import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Check,
  X,
  Sparkles,
  Minus,
  Plus,
  BookOpen,
  ChevronDown,
  Info,
} from "lucide-react";
import { WheelPicker } from "@/components/ui/WheelPicker";
import { getVowsByCategory, ALL_VOWS, type Vow } from "@/data/vows";
import { VowDetailDialog } from "@/components/VowDetailDialog";
import { usePractice } from "@/hooks/usePractice";
import { getRotationIndex, rotateSlice } from "@/hooks/useFocus";
import { useFavoritesStore } from "@/stores/useFavoritesStore";
import { cn } from "@/lib/utils";

type Step = "form" | "success";
type EntryType = "positive" | "negative" | "meditation";

const ROTATION_ENABLED = true;

function toLocalISO(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EntryForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { type?: EntryType; slot?: string; meditationDauer?: number; meditationArt?: string } | null;

  const { addEntry, completedSlots, currentTimeSlot, timeSlots } = usePractice();
  const tugenden = getVowsByCategory("ten-virtues");

  // If no type passed, redirect to journal
  useEffect(() => {
    if (!navState?.type) navigate("/", { replace: true });
  }, [navState?.type, navigate]);

  const entryType = navState?.type ?? "positive";

  // Step
  const [step, setStep] = useState<Step>("form");

  // Gelübde block
  const [vowAccordionOpen, setVowAccordionOpen] = useState(false);

  // Tugend-Info dialog
  const [infoVow, setInfoVow] = useState<Vow | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Slot
  const [selectedSlot, setSelectedSlot] = useState<string>(
    () => navState?.slot ?? String(currentTimeSlot.slot)
  );

  // Rotation — same formula as HomePage so Tugend/Gelübde always match
  const _d = new Date();
  const todayStr = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, "0")}-${String(_d.getDate()).padStart(2, "0")}`;
  const rotationIndex = getRotationIndex(todayStr, parseInt(selectedSlot, 10));
  const suggestedVirtueId = ROTATION_ENABLED
    ? (tugenden[rotationIndex % tugenden.length]?.id ?? null)
    : null;

  // Form fields
  const [text, setText] = useState("");
  const [widmung, setWidmung] = useState("");
  const [versprechen, setVersprechen] = useState("");
  const [versprechenBisDatum, setVersprechenBisDatum] = useState("");
  const [promise, setPromise] = useState("");
  const [promiseBisDatum, setPromiseBisDatum] = useState("");
  const [selectedVirtueIds, setSelectedVirtueIds] = useState<number[]>([]);

  // Meditation-specific fields
  const [meditationArt, setMeditationArt] = useState(navState?.meditationArt ?? "");
  const [meditationDauer, setMeditationDauer] = useState(navState?.meditationDauer ?? 0);

  // Duration stepper state — Ausgleichshandlung
  const [durDays, setDurDays] = useState(0);
  const [durHours, setDurHours] = useState(0);
  const [durMins, setDurMins] = useState(0);
  const [ausgleichTimeOpen, setAusgleichTimeOpen] = useState(false);

  // Duration stepper state — Versprechen
  const [durPromiseDays, setDurPromiseDays] = useState(0);
  const [durPromiseHours, setDurPromiseHours] = useState(0);
  const [durPromiseMins, setDurPromiseMins] = useState(0);
  const [promiseTimeOpen, setPromiseTimeOpen] = useState(true);

  const [saving, setSaving] = useState(false);

  // Auto-set rotation virtue on first render
  const hasAutoSelectedVirtue = useRef(false);
  useEffect(() => {
    if (suggestedVirtueId && !hasAutoSelectedVirtue.current) {
      setSelectedVirtueIds([suggestedVirtueId]);
      hasAutoSelectedVirtue.current = true;
    }
  }, [suggestedVirtueId]);

  // Duration stepper: update datetime from stepper values
  const stepDur = useCallback(
    (field: "days" | "hours" | "mins", delta: number) => {
      setDurDays((prev) => {
        const newDays = field === "days" ? Math.max(0, prev + delta) : prev;
        setDurHours((prevH) => {
          const newHours =
            field === "hours" ? Math.max(0, Math.min(23, prevH + delta)) : prevH;
          setDurMins((prevM) => {
            const newMins =
              field === "mins" ? Math.max(0, Math.min(59, prevM + delta)) : prevM;
            const total = newDays * 24 * 60 + newHours * 60 + newMins;
            setVersprechenBisDatum(
              total > 0 ? toLocalISO(new Date(Date.now() + total * 60_000)) : ""
            );
            return newMins;
          });
          return newHours;
        });
        return newDays;
      });
    },
    []
  );

  // Datetime picker: update stepper from datetime value — Ausgleichshandlung
  const onDatetimeChange = useCallback((value: string) => {
    setVersprechenBisDatum(value);
    if (!value) {
      setDurDays(0); setDurHours(0); setDurMins(0);
      return;
    }
    const diffMs = new Date(value).getTime() - Date.now();
    if (diffMs <= 0) return;
    const totalMins = Math.round(diffMs / 60_000);
    setDurDays(Math.floor(totalMins / (24 * 60)));
    setDurHours(Math.floor((totalMins % (24 * 60)) / 60));
    setDurMins(totalMins % 60);
  }, []);

  // Duration stepper — Versprechen
  const stepDurPromise = useCallback(
    (field: "days" | "hours" | "mins", delta: number) => {
      setDurPromiseDays((prev) => {
        const newDays = field === "days" ? Math.max(0, prev + delta) : prev;
        setDurPromiseHours((prevH) => {
          const newHours =
            field === "hours" ? Math.max(0, Math.min(23, prevH + delta)) : prevH;
          setDurPromiseMins((prevM) => {
            const newMins =
              field === "mins" ? Math.max(0, Math.min(59, prevM + delta)) : prevM;
            const total = newDays * 24 * 60 + newHours * 60 + newMins;
            setPromiseBisDatum(
              total > 0 ? toLocalISO(new Date(Date.now() + total * 60_000)) : ""
            );
            return newMins;
          });
          return newHours;
        });
        return newDays;
      });
    },
    []
  );

  // Datetime picker — Versprechen
  const onPromiseDatetimeChange = useCallback((value: string) => {
    setPromiseBisDatum(value);
    if (!value) {
      setDurPromiseDays(0); setDurPromiseHours(0); setDurPromiseMins(0);
      return;
    }
    const diffMs = new Date(value).getTime() - Date.now();
    if (diffMs <= 0) return;
    const totalMins = Math.round(diffMs / 60_000);
    setDurPromiseDays(Math.floor(totalMins / (24 * 60)));
    setDurPromiseHours(Math.floor((totalMins % (24 * 60)) / 60));
    setDurPromiseMins(totalMins % 60);
  }, []);

  const handleSlotChange = useCallback((value: string) => {
    setSelectedSlot(value);
  }, []);

  // Meditation: always saveable; positive/negative: text required
  const isFormValid = entryType === "meditation" ? true : text.trim().length > 0;

  const handleSave = useCallback(async () => {
    if (!isFormValid) return;
    setSaving(true);
    try {
      const slotNum = Number(selectedSlot) as 1 | 2 | 3 | 4 | 5 | 6;
      const d = new Date();
      const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      await addEntry({
        date: localDate,
        timeSlot: slotNum,
        type: entryType,
        text: text.trim(),
        virtueIds: entryType !== "meditation" && selectedVirtueIds.length > 0 ? selectedVirtueIds : undefined,
        widmung: entryType === "positive" ? widmung.trim() : "",
        versprechen: entryType === "negative" ? versprechen.trim() : "",
        versprechenBisDatum:
          entryType === "negative" && versprechenBisDatum
            ? versprechenBisDatum
            : undefined,
        versprechenErfuellt: entryType === "negative" ? false : undefined,
        promise: entryType === "negative" && promise.trim() ? promise.trim() : undefined,
        promiseBisDatum:
          entryType === "negative" && promiseBisDatum ? promiseBisDatum : undefined,
        promiseErfuellt: entryType === "negative" && (promise.trim() || promiseBisDatum) ? false : undefined,
        meditationArt: entryType === "meditation" ? (meditationArt.trim() || "Meditation") : undefined,
        meditationDauer: entryType === "meditation" && meditationDauer > 0 ? meditationDauer : undefined,
      });
      setStep("success");
    } catch (err) {
      console.error("Failed to save entry:", err);
    } finally {
      setSaving(false);
    }
  }, [
    entryType,
    isFormValid,
    selectedSlot,
    text,
    widmung,
    versprechen,
    versprechenBisDatum,
    promise,
    promiseBisDatum,
    selectedVirtueIds,
    meditationArt,
    meditationDauer,
    addEntry,
  ]);

  const resetForm = useCallback(() => {
    setText("");
    setWidmung("");
    setVersprechen("");
    setVersprechenBisDatum("");
    setPromise("");
    setPromiseBisDatum("");
    setSelectedVirtueIds(suggestedVirtueId ? [suggestedVirtueId] : []);
    setDurDays(0);
    setDurHours(0);
    setDurMins(0);
    setDurPromiseDays(0);
    setDurPromiseHours(0);
    setDurPromiseMins(0);
    setSelectedSlot(String(currentTimeSlot.slot));
  }, [suggestedVirtueId, currentTimeSlot.slot]);

  // ── Form ──────────────────────────────────────────────────────────────────
  if (step === "form") {
    const isMeditation = entryType === "meditation";
    const isPositive = entryType === "positive";
    const accentColor = isPositive ? "emerald" : isMeditation ? "cyan" : "rose";

    return (
      <div className="min-h-screen bg-white dark:bg-[#0D1117] px-4 py-6">
        <div className="mx-auto max-w-lg space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {isMeditation ? "Meditation" : isPositive ? "Positiver Eintrag" : "Negativer Eintrag"}
              </h1>
            </div>
            <Badge
              variant="outline"
              className={`text-${accentColor}-600 dark:text-${accentColor}-400 border-${accentColor}-500/40`}
            >
              {isMeditation ? "🧘" : isPositive ? "Positiv" : "Negativ"}
            </Badge>
          </div>

          {/* Time Slot */}
          <Card className="border-gray-200 dark:border-[#1E3A5F]/50">
            <CardContent className="pt-4 pb-4">
              <Select value={selectedSlot} onValueChange={handleSlotChange}>
                <SelectTrigger className="h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((ts) => {
                    const isDone = completedSlots.has(ts.slot);
                    const isCurrent = ts.slot === currentTimeSlot.slot;
                    return (
                      <SelectItem key={ts.slot} value={String(ts.slot)}>
                        <span className="flex items-center gap-2">
                          {ts.emoji} {ts.label}
                          {isDone && (
                            <Badge variant="outline" className="text-xs text-emerald-600 dark:text-emerald-400 border-emerald-500/40">
                              hat Einträge
                            </Badge>
                          )}
                          {isCurrent && !isDone && (
                            <Badge variant="outline" className="text-xs text-cyan-600 dark:text-vajra-cyan border-vajra-cyan/40">
                              aktuell
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Gelübde Block (F-2) — nur bei positiv/negativ */}
          {!isMeditation && (
            <VowFormBlock open={vowAccordionOpen} onToggle={() => setVowAccordionOpen((p) => !p)} rotationIndex={rotationIndex} />
          )}

          {/* Virtue Selector — nur bei positiv/negativ */}
          {!isMeditation && (
            <Card className="border-gray-200 dark:border-[#1E3A5F]/50">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-500 dark:text-muted-foreground">
                    Welche Tugenden?{" "}
                    <span className="text-xs text-gray-400 dark:text-muted-foreground/60">(optional)</span>
                  </Label>
                  {selectedVirtueIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedVirtueIds([])}
                      className="text-xs text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      Alle abwaehlen
                    </button>
                  )}
                </div>
                {ROTATION_ENABLED && suggestedVirtueId && (
                  <p className="text-xs text-amber-600 dark:text-amber-400/70">
                    Rotation: Tugend {tugenden.find((v) => v.id === suggestedVirtueId)?.id} vorausgewaehlt
                  </p>
                )}
                <div className="grid grid-cols-2 gap-1.5">
                  {tugenden.map((vow: Vow) => {
                    const isSelected = selectedVirtueIds.includes(vow.id);
                    const isSuggested = ROTATION_ENABLED && vow.id === suggestedVirtueId;
                    return (
                      <div
                        key={vow.id}
                        className={cn(
                          "flex overflow-hidden rounded-lg border transition-colors",
                          isSelected
                            ? `border-${accentColor}-500/40 bg-${accentColor}-500/8`
                            : isSuggested
                            ? "border-amber-400/50 bg-amber-50/30 dark:bg-amber-500/5"
                            : "border-gray-200 dark:border-border/50"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedVirtueIds((prev) =>
                              prev.includes(vow.id)
                                ? prev.filter((id) => id !== vow.id)
                                : [...prev, vow.id]
                            )
                          }
                          className="flex flex-1 items-center gap-1.5 px-2 py-1.5 min-w-0 text-left"
                        >
                          <span
                            className={cn(
                              "flex h-3.5 w-3.5 shrink-0 rounded-full border-2 transition-colors",
                              isSelected
                                ? `border-${accentColor}-500 bg-${accentColor}-500`
                                : isSuggested
                                ? "border-amber-400"
                                : "border-gray-300 dark:border-border"
                            )}
                          />
                          <span
                            className={cn(
                              "text-xs font-medium truncate",
                              isSelected
                                ? `text-${accentColor}-700 dark:text-${accentColor}-400`
                                : isSuggested
                                ? "text-amber-700 dark:text-amber-400"
                                : "text-gray-600 dark:text-muted-foreground"
                            )}
                          >
                            {vow.de.title.replace("Kein ", "").replace("Keine ", "")}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setInfoVow(vow); setInfoOpen(true); }}
                          className="flex w-7 shrink-0 items-center justify-center border-l border-gray-200 dark:border-border/50 text-gray-400 dark:text-muted-foreground hover:text-vajra-blue dark:hover:text-vajra-cyan hover:bg-vajra-blue/5 transition-colors"
                          aria-label={`Info: ${vow.de.title}`}
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tugend Info Dialog */}
          <VowDetailDialog
            vow={infoVow}
            open={infoOpen}
            onOpenChange={setInfoOpen}
            isFavorite={infoVow ? false : false}
            onToggleFavorite={() => {}}
          />

          {/* Meditation-specific fields */}
          {isMeditation && (
            <Card className="border-vajra-cyan/30 bg-vajra-cyan/5">
              <CardContent className="pt-5 space-y-4">
                {/* Art */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-vajra-cyan">
                    Art der Meditation{" "}
                    <span className="text-xs font-normal text-vajra-cyan/60 dark:text-vajra-cyan/50">(optional)</span>
                  </Label>
                  <input
                    type="text"
                    placeholder="z.B. Shamatha, Vipassana, Tonglen..."
                    value={meditationArt}
                    onChange={(e) => setMeditationArt(e.target.value)}
                    className="w-full h-10 rounded-md border border-vajra-cyan/20 bg-white/50 dark:bg-background/50 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-vajra-cyan/40"
                  />
                </div>
                {/* Dauer — Drehrad */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-vajra-cyan">
                    Dauer <span className="text-xs font-normal text-vajra-cyan/60 dark:text-vajra-cyan/50">(optional)</span>
                  </Label>
                  <div className="rounded-xl border border-vajra-cyan/20 bg-white/50 dark:bg-background/50 overflow-hidden">
                    <WheelPicker
                      values={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 90, 105, 120]}
                      selected={meditationDauer}
                      onChange={setMeditationDauer}
                      suffix="min"
                      accentClass="text-vajra-cyan"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Text */}
          <Card className={`border-${accentColor}-500/30 bg-${accentColor}-50/50 dark:bg-${accentColor}-500/5`}>
            <CardContent className="pt-5 space-y-3">
              <Label className={`text-base font-semibold text-${accentColor}-700 dark:text-${accentColor}-400`}>
                {isMeditation ? "Notizen" : isPositive ? "Was habe ich Gutes getan?" : "Was habe ich falsch gemacht?"}
                {isMeditation && (
                  <span className="text-sm font-normal text-vajra-cyan/60 dark:text-vajra-cyan/50"> (optional)</span>
                )}
              </Label>
              <textarea
                placeholder={isMeditation ? "Beobachtungen, Erkenntnisse..." : isPositive ? "Beschreibe deine gute Tat..." : "Beschreibe was passiert ist..."}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className={`w-full min-h-[100px] rounded-md border bg-white/50 dark:bg-background/50 border-${accentColor}-500/20 focus:outline-none focus:ring-2 focus:ring-${accentColor}-500/40 resize-none px-3 py-2 text-sm text-gray-900 dark:text-gray-100`}
              />
            </CardContent>
          </Card>

          {/* Widmung (Positive only, optional) */}
          {isPositive && (
            <Card className="border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/5">
              <CardContent className="pt-5 space-y-3">
                <Label className="text-base font-semibold text-blue-700 dark:text-blue-400">
                  Wem widme ich diese Tat?{" "}
                  <span className="text-sm font-normal text-blue-600/70 dark:text-blue-400/60">(optional)</span>
                </Label>
                <textarea
                  placeholder="z.B. Allen Lebewesen, meiner Familie, meinem Lehrer..."
                  value={widmung}
                  onChange={(e) => setWidmung(e.target.value)}
                  className="w-full min-h-[60px] rounded-md border bg-white/50 dark:bg-background/50 border-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                />
              </CardContent>
            </Card>
          )}

          {/* Versprechen (Negative only, optional) */}
          {!isPositive && !isMeditation && (
            <Card className="border-vajra-blue/20 bg-vajra-blue/5 dark:bg-vajra-blue/5 dark:border-vajra-blue/20">
              <CardContent className="pt-5 space-y-4">
                <div className="space-y-3">
                  <Label className="text-base font-semibold text-vajra-blue dark:text-vajra-cyan">
                    Versprechen{" "}
                    <span className="text-sm font-normal text-vajra-blue/60 dark:text-vajra-cyan/50">(optional)</span>
                  </Label>
                  <p className="text-xs text-vajra-blue/70 dark:text-vajra-cyan/60 -mt-1">
                    Was verspreche ich zu tun oder zu lassen?
                  </p>
                  <textarea
                    placeholder="Ich werde es nicht mehr tun, bis..."
                    value={promise}
                    onChange={(e) => setPromise(e.target.value)}
                    className="w-full min-h-[60px] rounded-md border bg-white/50 dark:bg-background/50 border-vajra-blue/20 dark:border-vajra-blue/20 focus:outline-none focus:ring-2 focus:ring-vajra-blue/40 resize-none px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setPromiseTimeOpen((v) => !v)}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-muted-foreground"
                  >
                    Bis wann?{" "}
                    <span className="text-xs text-gray-400 dark:text-muted-foreground/60">(optional)</span>
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", promiseTimeOpen && "rotate-180")} />
                  </button>
                  {promiseTimeOpen && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        {(
                          [
                            { label: "Tage", value: durPromiseDays, field: "days" as const },
                            { label: "Stunden", value: durPromiseHours, field: "hours" as const },
                            { label: "Minuten", value: durPromiseMins, field: "mins" as const },
                          ] as const
                        ).map(({ label, value, field }) => (
                          <div key={field} className="flex flex-col items-center gap-1.5">
                            <div className="flex items-center border border-gray-200 dark:border-border/50 rounded-lg overflow-hidden">
                              <button type="button" onClick={() => stepDurPromise(field, -1)} className="w-8 h-9 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 dark:text-muted-foreground">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-10 h-9 flex items-center justify-center text-sm font-semibold text-gray-900 dark:text-white bg-white dark:bg-background border-x border-gray-200 dark:border-border/50 tabular-nums">{value}</span>
                              <button type="button" onClick={() => stepDurPromise(field, 1)} className="w-8 h-9 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 dark:text-muted-foreground">
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="text-xs text-gray-400 dark:text-muted-foreground">{label}</span>
                          </div>
                        ))}
                      </div>
                      <input
                        type="datetime-local"
                        value={promiseBisDatum}
                        onChange={(e) => onPromiseDatetimeChange(e.target.value)}
                        className="w-full h-10 rounded-md border border-gray-200 dark:border-border/50 bg-white/50 dark:bg-background/50 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-vajra-blue/40"
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ausgleichshandlung (Negative only, optional) */}
          {!isPositive && !isMeditation && (
            <Card className="border-amber-500/20 bg-amber-50/30 dark:bg-amber-500/5 dark:border-amber-500/20">
              <CardContent className="pt-5 space-y-4">
                <div className="space-y-3">
                  <Label className="text-base font-semibold text-amber-700 dark:text-amber-400">
                    Ausgleichshandlung{" "}
                    <span className="text-sm font-normal text-amber-600/60 dark:text-amber-400/50">(optional)</span>
                  </Label>
                  <p className="text-xs text-amber-700/70 dark:text-amber-400/60 -mt-1">
                    Was werde ich aktiv tun, um das auszugleichen?
                  </p>
                  <textarea
                    placeholder="Ich denke über Leerheit nach."
                    value={versprechen}
                    onChange={(e) => setVersprechen(e.target.value)}
                    className="w-full min-h-[60px] rounded-md border bg-white/50 dark:bg-background/50 border-amber-500/20 dark:border-amber-500/20 focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setAusgleichTimeOpen((v) => !v)}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-muted-foreground"
                  >
                    Bis wann?{" "}
                    <span className="text-xs text-gray-400 dark:text-muted-foreground/60">(optional)</span>
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", ausgleichTimeOpen && "rotate-180")} />
                  </button>
                  {ausgleichTimeOpen && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        {(
                          [
                            { label: "Tage", value: durDays, field: "days" as const },
                            { label: "Stunden", value: durHours, field: "hours" as const },
                            { label: "Minuten", value: durMins, field: "mins" as const },
                          ] as const
                        ).map(({ label, value, field }) => (
                          <div key={field} className="flex flex-col items-center gap-1.5">
                            <div className="flex items-center border border-gray-200 dark:border-border/50 rounded-lg overflow-hidden">
                              <button type="button" onClick={() => stepDur(field, -1)} className="w-8 h-9 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 dark:text-muted-foreground">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-10 h-9 flex items-center justify-center text-sm font-semibold text-gray-900 dark:text-white bg-white dark:bg-background border-x border-gray-200 dark:border-border/50 tabular-nums">{value}</span>
                              <button type="button" onClick={() => stepDur(field, 1)} className="w-8 h-9 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 dark:text-muted-foreground">
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <span className="text-xs text-gray-400 dark:text-muted-foreground">{label}</span>
                          </div>
                        ))}
                      </div>
                      <input
                        type="datetime-local"
                        value={versprechenBisDatum}
                        onChange={(e) => onDatetimeChange(e.target.value)}
                        className="w-full h-10 rounded-md border border-gray-200 dark:border-border/50 bg-white/50 dark:bg-background/50 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="sticky bottom-4 pt-2">
            <Button
              className={`w-full h-14 text-lg shadow-lg ${
                isPositive
                  ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-900/30"
                  : "bg-rose-600 hover:bg-rose-700 shadow-rose-900/30"
              }`}
              onClick={handleSave}
              disabled={saving || !isFormValid}
            >
              {saving ? (
                "Wird gespeichert..."
              ) : (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  Eintrag speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white dark:bg-[#0D1117] flex items-center justify-center px-4">
      <div className="mx-auto max-w-lg w-full text-center space-y-6">
        <div className="relative inline-flex">
          <div className="h-24 w-24 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
            <Sparkles className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            Eintrag gespeichert!
          </h1>
          <p className="text-gray-500 dark:text-muted-foreground">
            Gut gemacht. Weiter so!
          </p>
        </div>

        <div className="space-y-3">
          {/* Zum Journal */}
          <Button
            className="w-full h-14 text-lg bg-vajra-blue hover:bg-vajra-blue/90"
            onClick={() => navigate("/")}
          >
            Zum Journal
          </Button>

          {/* Weiterer Eintrag — Split */}
          <p className="text-xs text-gray-400 dark:text-muted-foreground">Weiterer Eintrag:</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                resetForm();
                setStep("form");
                navigate("/entry", { state: { type: "positive", slot: String(currentTimeSlot.slot) } });
              }}
              className="rounded-xl border-2 border-emerald-500/30 bg-emerald-50/80 dark:bg-emerald-500/5 px-4 py-4 text-center transition-all hover:border-emerald-500/60 active:scale-[0.97]"
            >
              <div className="flex flex-col items-center gap-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/20">
                  <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-base font-semibold text-emerald-700 dark:text-emerald-400">Positiv</p>
                <p className="text-xs text-emerald-600/60 dark:text-emerald-400/50">Gutes getan</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setStep("form");
                navigate("/entry", { state: { type: "negative", slot: String(currentTimeSlot.slot) } });
              }}
              className="rounded-xl border-2 border-rose-500/30 bg-rose-50/80 dark:bg-rose-500/5 px-4 py-4 text-center transition-all hover:border-rose-500/60 active:scale-[0.97]"
            >
              <div className="flex flex-col items-center gap-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500/20">
                  <X className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                </div>
                <p className="text-base font-semibold text-rose-700 dark:text-rose-400">Negativ</p>
                <p className="text-xs text-rose-600/60 dark:text-rose-400/50">Fehler bemerkt</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gelübde block for EntryForm (F-2) — category-based, clickable items
// ---------------------------------------------------------------------------

function VowFormBlock({
  open,
  onToggle,
  rotationIndex,
}: {
  open: boolean;
  onToggle: () => void;
  rotationIndex: number;
}) {
  const [selectedVow, setSelectedVow] = useState<Vow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavoritesStore();

  const enabled = localStorage.getItem("vajra-vow-slot-enabled") === "true";
  const categories: string[] = JSON.parse(localStorage.getItem("vajra-vow-slot-categories") || "[]");
  const order = localStorage.getItem("vajra-vow-slot-order") || "sequential";

  if (!enabled || categories.length === 0) return null;

  const pool = ALL_VOWS.filter((v) => categories.includes(v.category) && v.category !== "ten-virtues");
  const count = parseInt(localStorage.getItem("vajra-vow-slot-count") || "3", 10);

  let vows: typeof pool;
  if (order === "random") {
    // Deterministic LCG shuffle — same as HomePage
    const seed = rotationIndex;
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.abs((seed * (i + 1) * 1664525 + 1013904223) % (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    vows = shuffled.slice(0, count);
  } else {
    vows = rotateSlice(pool, count, rotationIndex);
  }

  if (vows.length === 0) return null;

  return (
    <>
      <Card className="border-vajra-blue/20 dark:border-vajra-blue/20">
        <CardContent className="pt-4 pb-4 space-y-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-between"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-vajra-blue dark:text-vajra-cyan">
              <BookOpen className="h-4 w-4" />
              Gelübde ({vows.length})
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-gray-400 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </button>

          {open && (
            <div className="space-y-2">
              {vows.map((vow) => (
                <button
                  key={vow.id}
                  type="button"
                  onClick={() => { setSelectedVow(vow); setDetailOpen(true); }}
                  className="w-full flex items-center gap-2 rounded-lg bg-vajra-blue/5 dark:bg-vajra-blue/10 px-3 py-2.5 border border-vajra-blue/10 dark:border-vajra-blue/20 hover:bg-vajra-blue/10 dark:hover:bg-vajra-blue/15 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {vow.de.title}
                    </p>
                    {vow.de.positiveFormulation && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-muted-foreground line-clamp-2">
                        {vow.de.positiveFormulation}
                      </p>
                    )}
                  </div>
                  <span className="text-vajra-blue/50 dark:text-vajra-cyan/50 text-sm shrink-0">›</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <VowDetailDialog
        vow={selectedVow}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        isFavorite={selectedVow ? isFavorite(selectedVow.id) : false}
        onToggleFavorite={toggleFavorite}
      />
    </>
  );
}
